import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Ticket, Plus, Pencil, Trash2, Search, Loader2, 
  Calendar, Hash, Percent, Euro, CheckCircle, XCircle, 
  ArrowLeft, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminVouchers() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_booking_amount: 0,
    max_uses: '',
    valid_until: '',
    description: '',
    is_active: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchVouchers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeOnly) params.append('active_only', 'true');
      
      const response = await fetch(`${API_URL}/api/admin/vouchers?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/admin/login');
          return;
        }
        throw new Error('Failed to fetch vouchers');
      }

      const data = await response.json();
      setVouchers(data.vouchers || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [activeOnly, navigate]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_booking_amount: 0,
      max_uses: '',
      valid_until: '',
      description: '',
      is_active: true
    });
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_until: formData.valid_until || null
      };

      const response = await fetch(`${API_URL}/api/admin/vouchers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create voucher');
      }

      toast.success('Voucher created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedVoucher) return;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_until: formData.valid_until || null
      };

      const response = await fetch(`${API_URL}/api/admin/vouchers/${selectedVoucher.voucher_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update voucher');
      }

      toast.success('Voucher updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVoucher) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/vouchers/${selectedVoucher.voucher_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to delete voucher');
      }

      toast.success('Voucher deleted');
      setDeleteDialogOpen(false);
      setSelectedVoucher(null);
      fetchVouchers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (voucher) => {
    setSelectedVoucher(voucher);
    setFormData({
      code: voucher.code,
      discount_type: voucher.discount_type,
      discount_value: voucher.discount_value,
      min_booking_amount: voucher.min_booking_amount || 0,
      max_uses: voucher.max_uses?.toString() || '',
      valid_until: voucher.valid_until?.split('T')[0] || '',
      description: voucher.description || '',
      is_active: voucher.is_active
    });
    setEditDialogOpen(true);
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast.success('Code copied!');
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const filteredVouchers = vouchers.filter(v => 
    v.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No expiration';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-background py-8" data-testid="admin-vouchers-page">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                <Ticket className="w-8 h-8 text-accent" />
                Voucher Management
              </h1>
              <p className="text-muted-foreground mt-1">Create and manage discount vouchers for clients</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} data-testid="create-voucher-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Voucher
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search vouchers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="voucher-search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="active-only"
                  checked={activeOnly}
                  onCheckedChange={setActiveOnly}
                />
                <Label htmlFor="active-only" className="text-sm">Active only</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vouchers List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : filteredVouchers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Ticket className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No vouchers found</h3>
              <p className="text-muted-foreground mb-4">Create your first voucher to offer discounts to clients</p>
              <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Voucher
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredVouchers.map((voucher) => (
              <Card key={voucher.voucher_id} className={`transition-all ${!voucher.is_active || isExpired(voucher.valid_until) ? 'opacity-60' : ''}`} data-testid={`voucher-card-${voucher.code}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center">
                        {voucher.discount_type === 'percentage' ? (
                          <Percent className="w-8 h-8 text-accent" />
                        ) : (
                          <Euro className="w-8 h-8 text-accent" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-xl font-mono font-bold bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded">
                            {voucher.code}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => copyCode(voucher.code)}
                          >
                            {copiedCode === voucher.code ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-2xl font-bold text-accent">
                          {voucher.discount_type === 'percentage' 
                            ? `${voucher.discount_value}% OFF`
                            : `${voucher.discount_value}€ OFF`
                          }
                        </p>
                        {voucher.description && (
                          <p className="text-sm text-muted-foreground mt-1">{voucher.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Hash className="w-4 h-4 text-muted-foreground" />
                          <span>Used: {voucher.used_count || 0}{voucher.max_uses ? ` / ${voucher.max_uses}` : ''}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className={isExpired(voucher.valid_until) ? 'text-red-500' : ''}>
                            {formatDate(voucher.valid_until)}
                          </span>
                        </div>
                        {voucher.min_booking_amount > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Euro className="w-4 h-4 text-muted-foreground" />
                            <span>Min: {voucher.min_booking_amount}€</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {voucher.is_active && !isExpired(voucher.valid_until) ? (
                          <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : isExpired(voucher.valid_until) ? (
                          <Badge variant="destructive">
                            <XCircle className="w-3 h-3 mr-1" />
                            Expired
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => openEditDialog(voucher)}
                          data-testid={`edit-voucher-${voucher.code}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => { setSelectedVoucher(voucher); setDeleteDialogOpen(true); }}
                          data-testid={`delete-voucher-${voucher.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-accent" />
                Create New Voucher
              </DialogTitle>
              <DialogDescription>
                Create a discount voucher for your clients
              </DialogDescription>
            </DialogHeader>
            <VoucherForm formData={formData} setFormData={setFormData} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving || !formData.code}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Voucher
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-accent" />
                Edit Voucher
              </DialogTitle>
              <DialogDescription>
                Update voucher details
              </DialogDescription>
            </DialogHeader>
            <VoucherForm formData={formData} setFormData={setFormData} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-500 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Delete Voucher
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the voucher <strong>{selectedVoucher?.code}</strong>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function VoucherForm({ formData, setFormData }) {
  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="code">Voucher Code *</Label>
        <Input
          id="code"
          placeholder="SUMMER2024"
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
          className="font-mono uppercase"
          data-testid="voucher-code-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Discount Type</Label>
          <Select
            value={formData.discount_type}
            onValueChange={(v) => setFormData(prev => ({ ...prev, discount_type: v }))}
          >
            <SelectTrigger data-testid="voucher-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Percentage (%)</SelectItem>
              <SelectItem value="fixed">Fixed Amount (€)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="discount_value">
            Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(€)'}
          </Label>
          <Input
            id="discount_value"
            type="number"
            min="1"
            max={formData.discount_type === 'percentage' ? 100 : undefined}
            value={formData.discount_value}
            onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
            data-testid="voucher-value-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="min_booking">Minimum Booking (€)</Label>
          <Input
            id="min_booking"
            type="number"
            min="0"
            value={formData.min_booking_amount}
            onChange={(e) => setFormData(prev => ({ ...prev, min_booking_amount: parseFloat(e.target.value) || 0 }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="max_uses">Max Uses (empty = unlimited)</Label>
          <Input
            id="max_uses"
            type="number"
            min="1"
            placeholder="Unlimited"
            value={formData.max_uses}
            onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="valid_until">Valid Until</Label>
        <Input
          id="valid_until"
          type="date"
          value={formData.valid_until}
          onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          placeholder="Summer sale discount"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        />
      </div>

      <div className="flex items-center justify-between pt-2">
        <Label htmlFor="is_active">Active</Label>
        <Switch
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))}
        />
      </div>
    </div>
  );
}
